import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Search, Link2, Unlink, Ban, RefreshCw, CheckCircle2,
  Rocket, AlertCircle, Calendar as CalendarIcon, Info, X, RotateCcw,
  ArrowUpDown, Filter, Undo2, CopyCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  loadPaymentsWizard,
  matchWizardTransaction,
  unmatchWizardTransaction,
  dismissWizardTransaction,
  bulkDismissWizardTransactions,
  dedupeWizardDuplicates,
  wizardBorrowerSearch,
  postAllPaymentsWizard,
  getPostAllStatus,
  postAnywayWizardTransaction,
  previewRollback,
  startRollback,
  getRollbackStatus,
} from "@/lib/api";
import { toast } from "sonner";
import PortalBankFeedStrip from "@/components/portal_bank_feed/PortalBankFeedStrip";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const fmtGBP = (n) => new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", minimumFractionDigits: 2,
}).format(Number(n || 0));

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};

const STATUS_STYLES = {
  matched:        { label: "Ready",              cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  manual_matched: { label: "Ready (manual)",     cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  unmatched:      { label: "Needs match",        cls: "bg-amber-100 text-amber-700 border-amber-300" },
  duplicate:      { label: "Already in LoanDisk", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  posted:         { label: "Posted",             cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

// ─── Match dialog ─────────────────────────────────────────────────────
function MatchDialog({ open, txn, onClose, onMatched, wizardDate }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [posting, setPosting] = useState(null);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
    const desc = txn?.description || "";
    const m = desc.match(/SF[-\s]?(\d{3,6})/i);
    if (m) setQ(`SF-${m[1]}`);
  }, [open, txn]);

  const search = useCallback(async (qq) => {
    if (!qq || qq.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const r = await wizardBorrowerSearch(qq.trim());
      setResults(r?.results || []);
    } catch (e) {
      toast.error("Search failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { search(q); }, 300);
    return () => clearTimeout(t);
  }, [q, open, search]);

  const pick = async (loan) => {
    setPosting(loan.loan_id);
    try {
      await matchWizardTransaction(txn.transaction_id, {
        loan_id: String(loan.loan_id),
        loan_application_id: loan.loan_application_id || null,
        date: wizardDate || null,
      });
      toast.success(`Matched to ${loan.loan_application_id || loan.loan_id}`);
      onMatched();
      onClose();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Match failed"));
    } finally { setPosting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col" data-testid="pw-match-dialog">
        <DialogHeader>
          <DialogTitle>Match transaction to loan</DialogTitle>
          <DialogDescription className="space-y-0.5">
            <span className="block truncate">{txn?.description || "—"}</span>
            <span className="block font-semibold text-foreground">{fmtGBP(txn?.amount)} · {fmtDate(txn?.date)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by borrower name or SF# (e.g. SF-22869)"
            className="pl-9"
            data-testid="pw-match-search-input"
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 border rounded-md p-2">
          {busy && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}
          {!busy && results.length === 0 && q.trim().length >= 2 && (
            <p className="text-sm text-muted-foreground text-center py-6">No matches for &quot;{q}&quot;</p>
          )}
          {!busy && q.trim().length < 2 && (
            <p className="text-xs text-muted-foreground text-center py-6">Start typing an SF# or borrower name…</p>
          )}
          {results.map((loan) => (
            <div
              key={loan.loan_id}
              className="flex items-center justify-between gap-3 p-2.5 rounded-md border hover:bg-slate-50"
              data-testid={`pw-loan-result-${loan.loan_id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{loan.borrower_name}</p>
                <p className="text-xs text-muted-foreground">
                  {loan.loan_application_id} · {loan.loan_status_label}
                  {loan.days_past_due > 0 && (
                    <span className="ml-1 text-amber-600"> · {loan.days_past_due}d past due</span>
                  )}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold">{fmtGBP(loan.balance_amount)}</p>
                <p className="text-[10px] text-muted-foreground">balance</p>
              </div>
              <Button
                size="sm"
                onClick={() => pick(loan)}
                disabled={posting !== null}
                data-testid={`pw-pick-loan-${loan.loan_id}`}
              >
                {posting === loan.loan_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Link2 className="w-4 h-4 mr-1" />Match</>}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dismiss dialog ───────────────────────────────────────────────────
function DismissDialog({ open, txn, onClose, onDismissed, actioningUser, wizardDate }) {
  const [learn, setLearn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setLearn(false); }, [open]);

  const dismiss = async () => {
    setBusy(true);
    try {
      await dismissWizardTransaction(txn.transaction_id, {
        dismissed_by: actioningUser || "",
        learn_pattern: learn,
        date: wizardDate || null,
      });
      toast.success(learn
        ? "Dismissed and pattern learned — recurring internal transfers will auto-hide"
        : "Dismissed for today");
      onDismissed();
      onClose();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Dismiss failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="pw-dismiss-dialog">
        <DialogHeader>
          <DialogTitle>Dismiss transaction</DialogTitle>
          <DialogDescription className="space-y-0.5">
            <span className="block truncate">{txn?.description || "—"}</span>
            <span className="block font-semibold text-foreground">{fmtGBP(txn?.amount)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 py-2">
          <Checkbox
            id="pw-learn-pattern"
            checked={learn}
            onCheckedChange={(v) => setLearn(!!v)}
            data-testid="pw-learn-pattern-checkbox"
          />
          <label htmlFor="pw-learn-pattern" className="text-sm cursor-pointer">
            <span className="font-medium">Don&apos;t show this type of payment again</span>
            <span className="block text-xs text-muted-foreground">
              Recurring internal transfers with a similar description + amount will be auto-hidden from
              tomorrow&apos;s list.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pw-dismiss-cancel-btn">Cancel</Button>
          <Button
            variant="destructive"
            onClick={dismiss}
            disabled={busy}
            data-testid="pw-dismiss-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-1" />Dismiss</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────
function Row({ row, onMatch, onUnmatch, onDismiss, onPostAnyway, postingAnyway, selected, onToggleSelect }) {
  const meta = STATUS_STYLES[row.status] || STATUS_STYLES.unmatched;
  const isPosted = row.status === "posted";
  const isDuplicate = row.status === "duplicate";
  return (
    <div
      className={`grid grid-cols-12 items-center gap-3 px-3 py-2.5 border-b last:border-b-0 text-sm ${
        isPosted ? "bg-slate-50/60 text-slate-500" : "hover:bg-slate-50/40"
      } ${selected ? "bg-amber-50/60" : ""}`}
      data-testid={`pw-row-${row.transaction_id}`}
    >
      <div className="col-span-4 min-w-0 flex items-center gap-2.5">
        {!isPosted ? (
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect(row)}
            className="shrink-0"
            data-testid={`pw-select-cb-${row.transaction_id}`}
          />
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <div className="min-w-0">
          <p className={`font-medium truncate ${isPosted ? "line-through" : ""}`}>{row.description || "Bank Transfer"}</p>
          <p className="text-xs text-muted-foreground truncate">
            {row.reference && `Ref: ${row.reference} · `}{fmtDate(row.date)}
            {row.bank_account && (
              <span
                className="ml-1.5 inline-block px-1.5 py-px rounded bg-sky-50 border border-sky-200 text-[10px] font-medium text-sky-700 align-middle"
                title={`Received into ${row.bank_account} — posted to LoanDisk with this bank account name in the notes`}
                data-testid={`pw-bank-${row.transaction_id}`}
              >
                {row.bank_account}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="col-span-2 text-right font-semibold tabular-nums">{fmtGBP(row.amount)}</div>
      <div className="col-span-4 min-w-0">
        {row.matched_loan ? (
          <>
            <p className="text-xs font-medium truncate">{row.matched_loan.borrower_name || "—"}</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {row.matched_loan.loan_application_id || row.matched_loan.loan_id}
              {row.match_type === "sf_ref" && (
                <span className="ml-1.5 text-emerald-600">· SF-# match</span>
              )}
              {row.match_type === "company_name" && (
                <span className="ml-1.5 text-emerald-600">· Company name match</span>
              )}
              {String(row.match_type || "").includes("open_relink") && (
                <span className="ml-1.5 text-indigo-600" title={`Original match ${row.relinked_from} is not an open loan — re-pointed at the current open loan`}>
                  · re-linked from {row.relinked_from} (restructured)
                </span>
              )}
            </p>
            {isDuplicate && row.duplicate_info && (
              <p className="text-[11px] text-blue-700 truncate" title={row.duplicate_info.existing_notes}>
                existing #{row.duplicate_info.existing_repayment_id}
                {row.duplicate_info.existing_collector && ` · by ${row.duplicate_info.existing_collector}`}
              </p>
            )}
          </>
        ) : (
          <>
            <span className="text-xs text-muted-foreground italic">No match</span>
            {row.unmatched_reason && (
              <p className="text-[11px] text-amber-700 truncate" title={row.unmatched_reason} data-testid={`pw-unmatched-reason-${row.transaction_id}`}>
                {row.unmatched_reason}
              </p>
            )}
          </>
        )}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        <Badge variant="outline" className={`${meta.cls} text-[10px] px-1.5`}>{meta.label}</Badge>
        {!isPosted && (
          <>
            {isDuplicate ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => onPostAnyway(row)}
                disabled={postingAnyway === row.transaction_id}
                title="Post to LoanDisk anyway — creates a second repayment for this loan/date/amount"
                data-testid={`pw-post-anyway-btn-${row.transaction_id}`}
              >
                {postingAnyway === row.transaction_id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><CopyCheck className="w-3.5 h-3.5 mr-1" />Post anyway</>}
              </Button>
            ) : row.status === "unmatched" ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2"
                onClick={() => onMatch(row)}
                data-testid={`pw-match-btn-${row.transaction_id}`}
              >
                <Link2 className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-muted-foreground hover:text-red-600"
                onClick={() => onUnmatch(row)}
                title="Unmatch"
                data-testid={`pw-unmatch-btn-${row.transaction_id}`}
              >
                <Unlink className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-muted-foreground hover:text-amber-700"
              onClick={() => onDismiss(row)}
              title="Dismiss (internal transfer)"
              data-testid={`pw-dismiss-btn-${row.transaction_id}`}
            >
              <Ban className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function LoanRepayments() {
  const { user } = useAuth();
  const yesterdayISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("date");
    return /^\d{4}-\d{2}-\d{2}$/.test(q || "") ? q : yesterdayISO();
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const [postProgress, setPostProgress] = useState(null);
  const [confirmPost, setConfirmPost] = useState(false);
  const [matchTxn, setMatchTxn] = useState(null);
  const [dismissTxn, setDismissTxn] = useState(null);
  const [postingAnyway, setPostingAnyway] = useState(null);

  // Undo (deletes exactly what this page posted for the date)
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoProgress, setUndoProgress] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    let attempt = 0;
    while (attempt < 2) {
      try {
        const r = await loadPaymentsWizard(date);
        setData(r);
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 2) {
          toast.error("Failed to load transactions");
        } else {
          await new Promise((res) => setTimeout(res, 800));
        }
      }
    }
    setLoading(false); setRefreshing(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || { total: 0, ready: 0, matched: 0, unmatched: 0, duplicate: 0, posted: 0 };
  const rows = data?.rows || [];
  const readyCount = counts.ready ?? counts.matched ?? 0;
  const unmatchedCount = counts.unmatched || 0;
  const duplicateCount = counts.duplicate || 0;
  const canPost = readyCount > 0 && !posting;

  // ── Search / filter / sort ──────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("status");

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === "unmatched" && r.status !== "unmatched") return false;
      if (statusFilter === "ready" && !["matched", "manual_matched"].includes(r.status)) return false;
      if (statusFilter === "duplicate" && r.status !== "duplicate") return false;
      if (statusFilter === "posted" && r.status !== "posted") return false;
      if (!q) return true;
      const hay = [
        r.description,
        r.matched_loan?.borrower_name,
        r.matched_loan?.loan_application_id,
        r.matched_loan?.loan_id,
        String(r.amount),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, statusFilter]);

  const orderedRows = useMemo(() => {
    const arr = [...filteredRows];
    if (sortBy === "amount_desc") return arr.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    if (sortBy === "amount_asc") return arr.sort((a, b) => (a.amount || 0) - (b.amount || 0));
    if (sortBy === "description") return arr.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
    const order = { unmatched: 0, duplicate: 1, matched: 2, manual_matched: 2, posted: 3 };
    return arr.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  }, [filteredRows, sortBy]);

  // ── Bulk selection ───────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectableIds = useMemo(
    () => orderedRows.filter((r) => r.status !== "posted").map((r) => r.transaction_id),
    [orderedRows]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const toggleSelect = (row) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.transaction_id)) next.delete(row.transaction_id);
      else next.add(row.transaction_id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
  };
  const handleBulkDismiss = async () => {
    setBulkBusy(true);
    try {
      const res = await bulkDismissWizardTransactions([...selectedIds], user?.email || "");
      toast.success(`${res.dismissed} transaction${res.dismissed === 1 ? "" : "s"} dismissed`);
      setSelectedIds(new Set());
      load(true);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Bulk dismiss failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  // ── Duplicate rows detection (same bank Transaction ID pulled >1x) ───
  const [deduping, setDeduping] = useState(false);
  const duplicateRowCount = useMemo(() => {
    const seen = {};
    rows.forEach((r) => { if (r.reference) seen[r.reference] = (seen[r.reference] || 0) + 1; });
    return Object.values(seen).filter((n) => n > 1).reduce((a, n) => a + (n - 1), 0);
  }, [rows]);
  const handleDedupe = async () => {
    setDeduping(true);
    try {
      const res = await dedupeWizardDuplicates(date);
      toast.success(`Removed ${res.deleted} duplicate row${res.deleted === 1 ? "" : "s"} (${res.duplicate_groups} transaction${res.duplicate_groups === 1 ? "" : "s"} affected)`);
      if ((res.posted_conflicts || []).length > 0) {
        toast.warning(`${res.posted_conflicts.length} transaction(s) have MULTIPLE copies already posted to LoanDisk — use Undo/rollback to fix those in LoanDisk`, { duration: 15000 });
      }
      setSelectedIds(new Set());
      load(true);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Duplicate cleanup failed"));
    } finally {
      setDeduping(false);
    }
  };

  const handleUnmatch = async (row) => {
    try {
      await unmatchWizardTransaction(row.transaction_id);
      toast.success("Match removed");
      await load(true);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Unmatch failed"));
    }
  };

  const handlePostAnyway = async (row) => {
    if (!window.confirm(
      `Post ${fmtGBP(row.amount)} to ${row.matched_loan?.loan_application_id || row.matched_loan?.loan_id} anyway?\n\n` +
      `LoanDisk already has repayment #${row.duplicate_info?.existing_repayment_id} for the same loan/date/amount — ` +
      "this will create a SECOND entry."
    )) return;
    setPostingAnyway(row.transaction_id);
    try {
      const r = await postAnywayWizardTransaction(row.transaction_id, {
        actioning_user: user?.email || "", date,
      });
      toast.success(`Posted — LoanDisk repayment #${r.repayment_id || "created"}`);
      await load(true);
      refreshUndoPreview();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Post anyway failed"));
    } finally {
      setPostingAnyway(null);
    }
  };

  const runPostAll = async () => {
    setPosting(true);
    setPostResult(null);
    setPostProgress(null);
    setConfirmPost(false);
    try {
      const r = await postAllPaymentsWizard({ actioning_user: user?.email || "", date });
      toast.info("Posting started — running in the background…");
      setPostProgress({ processed: 0, total: 0, pct: 0, preparing: true });

      const deadline = Date.now() + 15 * 60 * 1000;
      let final = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1500));
        let status;
        try { status = await getPostAllStatus(r.run_id); }
        catch { continue; }
        setPostProgress({
          processed: status.processed,
          total: status.total,
          pct: status.progress_pct,
          preparing: status.status === "preparing",
        });
        if (status.status === "done" || status.status === "failed") {
          final = status;
          break;
        }
      }

      if (final) {
        setPostResult(final);
        const skipped = final.skipped_duplicate_count || 0;
        if (final.status === "failed" && final.fatal_error) {
          toast.error(final.fatal_error);
        } else if (final.posted_count === 0 && skipped === 0 && (final.failed_count || 0) === 0) {
          toast.info(final.message || "Nothing to post.");
        } else if (final.ok) {
          toast.success(
            `Posted ${final.posted_count || 0} payment(s)` +
            (skipped > 0 ? ` · ${skipped} flagged as already in LoanDisk` : ""),
          );
        } else {
          toast.warning(
            `${final.posted_count || 0} posted · ${final.failed_count || 0} failed` +
            (skipped > 0 ? ` · ${skipped} flagged as already in LoanDisk` : ""),
          );
        }
        await load(true);
        refreshUndoPreview();
      } else {
        toast.warning(
          "Still running after 15 minutes — it will finish in the background. Refresh the page shortly.",
        );
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, "Post failed"));
    } finally {
      setPosting(false);
      setPostProgress(null);
    }
  };

  const retryFailed = async () => { await runPostAll(); };

  // ─── Undo ───────────────────────────────────────────────────────────
  const refreshUndoPreview = useCallback(() => {
    previewRollback(date)
      .then(setUndoPreview)
      .catch(() => {});
  }, [date]);

  useEffect(() => { refreshUndoPreview(); }, [refreshUndoPreview]);

  const runUndo = async () => {
    setUndoing(true);
    setUndoProgress(null);
    try {
      const r = await startRollback(date, user?.email || "");
      if (!r.run_id) {
        toast.info(r.message || "Nothing to undo");
        setUndoing(false);
        setUndoOpen(false);
        return;
      }
      toast.info(`Deleting ${r.total} LoanDisk repayment(s)…`);
      setUndoProgress({ processed: 0, total: r.total, pct: 0 });

      const deadline = Date.now() + 10 * 60 * 1000;
      let final = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1500));
        let status;
        try { status = await getRollbackStatus(r.run_id); }
        catch { continue; }
        setUndoProgress({
          processed: status.processed,
          total: status.total,
          pct: status.progress_pct,
        });
        if (status.status === "done" || status.status === "failed") {
          final = status;
          break;
        }
      }

      if (final) {
        if (final.ok) {
          toast.success(`Undone — ${final.deleted_count || 0} repayment(s) removed from LoanDisk`);
        } else {
          toast.warning(`${final.deleted_count || 0} removed · ${final.failed_count || 0} failed`);
        }
        setPostResult(null);
        await load(true);
        refreshUndoPreview();
      } else {
        toast.warning("Undo still running — check back in a moment.");
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, "Undo failed"));
    } finally {
      setUndoing(false);
      setUndoProgress(null);
      setUndoOpen(false);
    }
  };

  const setDay = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-4">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Loan Repayments</h1>
          <p className="text-sm text-muted-foreground">
            Bank feed syncs at 5am → review the matches → post to LoanDisk in one click.
          </p>
        </div>
      </div>

      {/* Native GoCardless bank feeds — status + 90-day countdown + reconnect */}
      <PortalBankFeedStrip />

      {/* Totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200" data-testid="lr-totals-count">
          <CardContent className="py-3 px-4">
            <p className="text-[11px] uppercase text-muted-foreground tracking-wide">Payments</p>
            <p className="text-2xl font-bold tabular-nums">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : counts.total}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {readyCount} ready · {unmatchedCount} need match
              {duplicateCount > 0 && ` · ${duplicateCount} already in LoanDisk`}
              {counts.dismissed > 0 && ` · ${counts.dismissed} hidden`}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="lr-totals-cleared">
          <CardContent className="py-3 px-4">
            <p className="text-[11px] uppercase text-muted-foreground tracking-wide">Total collected</p>
            <p className="text-2xl font-bold tabular-nums">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : fmtGBP(data?.totals?.value_cleared)}
            </p>
            {data?.totals?.value_dismissed > 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {fmtGBP(data.totals.value_dismissed)} internal / hidden · {fmtGBP(data.totals.value_cleared_raw)} in Airtable
              </p>
            ) : data?.totals?.value_matched != null ? (
              <p className="text-[11px] text-muted-foreground">
                {fmtGBP(data.totals.value_matched)} on matched rows
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="lr-totals-posted">
          <CardContent className="py-3 px-4">
            <p className="text-[11px] uppercase text-emerald-700 tracking-wide">Posted to LoanDisk</p>
            <p className="text-2xl font-bold tabular-nums text-emerald-800">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : fmtGBP(data?.totals?.value_posted)}
            </p>
            <p className="text-[11px] text-emerald-700/70">
              {counts.posted || 0} row{(counts.posted || 0) === 1 ? "" : "s"}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="lr-totals-due">
          <CardContent className="py-3 px-4">
            <p className="text-[11px] uppercase text-muted-foreground tracking-wide">Due that day (LoanDisk)</p>
            <p className="text-2xl font-bold tabular-nums">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : fmtGBP(data?.totals?.due_that_day)}
            </p>
            {data?.totals?.due_that_day > 0 && data?.totals?.value_cleared >= 0 && (
              <p className="text-[11px] text-muted-foreground">
                {Math.round(
                  (Math.min(data.totals.value_cleared, data.totals.due_that_day)
                    / Math.max(data.totals.due_that_day, 1)) * 100
                )}% of due received
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Header — date picker + counts + Post + Undo */}
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-muted-foreground" />
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={() => setDay(-1)}
                data-testid="pw-day-prev-btn"
              >‹</Button>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-40 h-8"
                data-testid="pw-date-input"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={() => setDay(1)}
                disabled={date >= yesterdayISO()}
                data-testid="pw-day-next-btn"
              >›</Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setDate(yesterdayISO())}
                data-testid="pw-today-btn"
              >
                Yesterday
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => load(true)}
                disabled={refreshing || loading}
                title="Refresh"
                data-testid="pw-refresh-btn"
              >
                {refreshing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="border-slate-300" data-testid="pw-count-total">
                {counts.total} total
              </Badge>
              <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700" data-testid="pw-count-matched">
                {readyCount} ready
              </Badge>
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700" data-testid="pw-count-unmatched">
                {unmatchedCount} need match
              </Badge>
              {duplicateCount > 0 && (
                <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700" data-testid="pw-count-duplicate">
                  {duplicateCount} already in LoanDisk
                </Badge>
              )}
              {counts.posted > 0 && (
                <Badge variant="outline" className="bg-slate-50 border-slate-300 text-slate-700" data-testid="pw-count-posted">
                  {counts.posted} posted
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={!canPost}
                onClick={() => setConfirmPost(true)}
                data-testid="pw-post-all-btn"
              >
                <Rocket className="w-4 h-4 mr-1.5" />
                Post {readyCount > 0 ? `${readyCount} ready` : ""} to LoanDisk
              </Button>
              {undoPreview?.count > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setUndoOpen(true)}
                  disabled={undoing || posting}
                  data-testid="pw-rollback-btn"
                  title="Delete every repayment this page posted for this date from LoanDisk"
                >
                  <Undo2 className="w-4 h-4 mr-1.5" />
                  Undo ({undoPreview.count})
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Post live progress banner */}
      {postProgress && (
        <Alert className="border-blue-300 bg-blue-50" data-testid="pw-post-progress-banner">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          <AlertTitle className="text-blue-800">
            {postProgress.preparing || !postProgress.total
              ? "Preparing the list…"
              : `Posting to LoanDisk · ${postProgress.processed} / ${postProgress.total} (${postProgress.pct}%)`}
          </AlertTitle>
          <AlertDescription className="mt-2">
            <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${postProgress.pct || 0}%` }}
                data-testid="pw-post-progress-bar"
              />
            </div>
            <p className="text-xs text-blue-700/80 mt-1">
              Running in the background · you can leave this page open or come back later.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Post result banners */}
      {postResult && !postResult.ok && (postResult.failed?.length > 0) && (
        <Alert className="border-red-300 bg-red-50" data-testid="pw-post-fail-banner">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertTitle className="text-red-800 flex items-center justify-between">
            <span>Posting finished with {postResult.failed.length} failure(s)</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={retryFailed} disabled={posting} data-testid="pw-retry-failed-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Retry failed
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPostResult(null)} data-testid="pw-clear-fail-banner-btn">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </AlertTitle>
          <AlertDescription className="mt-2 text-red-700 text-xs space-y-1 max-h-48 overflow-y-auto">
            {postResult.failed.map((f, i) => (
              <div key={i} className="flex items-start justify-between gap-2 py-1 border-t border-red-200 first:border-t-0">
                <span className="truncate">
                  {f.loan_application_id || f.transaction_id} · <span className="font-semibold">{fmtGBP(f.amount)}</span>
                </span>
                <span className="text-red-800 max-w-[60%] truncate" title={f.error}>{f.error}</span>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {postResult && postResult.ok && (postResult.posted_count > 0) && (
        <Alert className="border-emerald-300 bg-emerald-50" data-testid="pw-post-success-banner">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800 flex items-center justify-between">
            <span>Posted {postResult.posted_count} payment(s) to LoanDisk</span>
            <Button size="sm" variant="ghost" onClick={() => setPostResult(null)} data-testid="pw-clear-success-banner-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </AlertTitle>
          <AlertDescription className="text-emerald-700 text-xs">
            Repayment method: Online Transfer · Collector: {postResult.collector_id_used}
          </AlertDescription>
        </Alert>
      )}

      {postResult && postResult.skipped_duplicate_count > 0 && (
        <Alert className="border-blue-300 bg-blue-50" data-testid="pw-post-skipped-banner">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertTitle className="text-blue-800 flex items-center justify-between">
            <span>{postResult.skipped_duplicate_count} payment(s) already in LoanDisk — not posted</span>
            <Button size="sm" variant="ghost" onClick={() => setPostResult((p) => p ? { ...p, skipped_duplicate_count: 0, skipped_duplicates: [] } : p)} data-testid="pw-clear-skipped-banner-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </AlertTitle>
          <AlertDescription className="mt-2 text-blue-800 text-xs space-y-1 max-h-40 overflow-y-auto">
            <p className="text-blue-700/80">
              LoanDisk already has a repayment for the same loan, date and amount (probably keyed manually).
              They&apos;re flagged in the list below — use <strong>Post anyway</strong> on a row if it really is a
              separate payment.
            </p>
            {(postResult.skipped_duplicates || []).map((s, i) => (
              <div key={i} className="flex items-start justify-between gap-2 py-1 border-t border-blue-200 first:border-t-0">
                <span className="truncate">
                  {s.loan_application_id} · <span className="font-semibold">{fmtGBP(s.amount)}</span>
                </span>
                <span className="text-blue-900 max-w-[55%] truncate">
                  existing #{s.existing_repayment_id}
                  {s.existing_collector && ` · by ${s.existing_collector}`}
                </span>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Duplicate rows alert */}
      {duplicateRowCount > 0 && (
        <Alert className="border-red-200 bg-red-50" data-testid="pw-duplicates-alert">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Duplicate transactions detected</AlertTitle>
          <AlertDescription className="text-red-700">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {duplicateRowCount} extra row{duplicateRowCount === 1 ? "" : "s"} share a bank Transaction ID with
                another row — the bank feed pulled the same transactions in more than once.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
                onClick={handleDedupe}
                disabled={deduping}
                data-testid="pw-dedupe-btn"
              >
                {deduping
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><Ban className="w-3.5 h-3.5 mr-1" />Remove duplicates</>}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Rows table */}
      <Card>
        <CardHeader className="pb-2 gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Cleared transactions · {fmtDate(data?.date)}</CardTitle>
            <span className="text-xs text-muted-foreground" data-testid="lr-filtered-count">
              Showing {orderedRows.length} of {rows.length}
            </span>
          </div>

          {/* Search + filter + sort bar */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by description, borrower, SF# or amount…"
                className="pl-8 h-8 text-sm"
                data-testid="lr-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="lr-search-clear-btn"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="lr-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="lr-filter-all">All statuses</SelectItem>
                  <SelectItem value="unmatched" data-testid="lr-filter-unmatched">Needs match only</SelectItem>
                  <SelectItem value="ready" data-testid="lr-filter-ready">Ready only</SelectItem>
                  <SelectItem value="duplicate" data-testid="lr-filter-duplicate">Already in LoanDisk</SelectItem>
                  <SelectItem value="posted" data-testid="lr-filter-posted">Posted only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="lr-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status" data-testid="lr-sort-status">Sort: Status</SelectItem>
                  <SelectItem value="amount_desc" data-testid="lr-sort-amount-desc">Sort: Amount (high → low)</SelectItem>
                  <SelectItem value="amount_asc" data-testid="lr-sort-amount-asc">Sort: Amount (low → high)</SelectItem>
                  <SelectItem value="description" data-testid="lr-sort-description">Sort: Description (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-1 px-3 py-2 rounded-md bg-amber-50 border border-amber-200" data-testid="pw-bulk-bar">
              <span className="text-xs font-medium text-amber-800" data-testid="pw-bulk-count">
                {selectedIds.size} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={handleBulkDismiss}
                disabled={bulkBusy}
                data-testid="pw-bulk-dismiss-btn"
              >
                {bulkBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><Ban className="w-3.5 h-3.5 mr-1" />Dismiss selected</>}
              </Button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setSelectedIds(new Set())}
                data-testid="pw-bulk-clear-btn"
              >
                Clear selection
              </button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading transactions…</span>
            </div>
          ) : orderedRows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {rows.length === 0
                ? "No transactions for this date."
                : "No transactions match your search or filter."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-12 items-center gap-3 px-3 py-2 border-b bg-slate-50 text-[11px] font-medium text-muted-foreground uppercase">
                <div className="col-span-4 flex items-center gap-2.5">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    className="shrink-0"
                    title="Select all visible"
                    data-testid="pw-select-all-cb"
                  />
                  <span>Transaction</span>
                </div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-4">Matched loan</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              <div>
                {orderedRows.map((r) => (
                  <Row
                    key={r.transaction_id}
                    row={r}
                    onMatch={setMatchTxn}
                    onUnmatch={handleUnmatch}
                    onDismiss={setDismissTxn}
                    onPostAnyway={handlePostAnyway}
                    postingAnyway={postingAnyway}
                    selected={selectedIds.has(r.transaction_id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Match dialog */}
      <MatchDialog
        open={!!matchTxn}
        txn={matchTxn}
        onClose={() => setMatchTxn(null)}
        onMatched={() => load(true)}
        wizardDate={date}
      />
      {/* Dismiss dialog */}
      <DismissDialog
        open={!!dismissTxn}
        txn={dismissTxn}
        onClose={() => setDismissTxn(null)}
        onDismissed={() => load(true)}
        actioningUser={user?.email}
        wizardDate={date}
      />

      {/* Post confirm */}
      <Dialog open={confirmPost} onOpenChange={setConfirmPost}>
        <DialogContent className="max-w-md" data-testid="pw-post-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Post {readyCount} payment(s) to LoanDisk?</DialogTitle>
            <DialogDescription>
              Each ready transaction posts as an <strong>Online Transfer</strong> repayment,
              collected by <strong>{user?.name || user?.email || "you"}</strong>.
              Duplicates are blocked automatically — anything already in LoanDisk gets flagged instead of posted.
              {unmatchedCount > 0 && (
                <span className="block mt-1.5 text-amber-700">
                  {unmatchedCount} unmatched transaction(s) will be skipped and stay in the list.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPost(false)} data-testid="pw-post-cancel-btn">Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={runPostAll}
              disabled={posting}
              data-testid="pw-post-confirm-btn"
            >
              {posting
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Posting…</>
                : <><Rocket className="w-4 h-4 mr-1" />Post {readyCount}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo confirm + progress */}
      <Dialog open={undoOpen} onOpenChange={(o) => { if (!undoing) setUndoOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="pw-rollback-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Undo posting for {fmtDate(date)}?</DialogTitle>
            <DialogDescription>
              Deletes the <strong>{undoPreview?.count || 0}</strong> repayment(s) this page posted to
              LoanDisk for this date. Nothing else in LoanDisk is touched — entries Collections keyed
              manually are always left alone.
            </DialogDescription>
          </DialogHeader>
          {undoProgress && (
            <div className="space-y-1">
              <div className="text-xs text-slate-600">
                Deleting… {undoProgress.processed}/{undoProgress.total}
              </div>
              <div className="w-full bg-red-100 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-red-500 transition-all"
                     style={{ width: `${undoProgress.pct || 0}%` }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoOpen(false)}
                    disabled={undoing}
                    data-testid="pw-rollback-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={runUndo}
              disabled={undoing || (undoPreview?.count || 0) === 0}
              data-testid="pw-rollback-confirm-btn"
            >
              {undoing
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Deleting…</>
                : <><Undo2 className="w-4 h-4 mr-1" />Undo {undoPreview?.count || 0}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
